import type { Components, Theme, CSSObject } from "@mui/material/styles";
import type { SemanticPalette } from "../../tokens/colorSemantic";
import { type StateSpec, stateToCss } from "../helpers";
import { RadiusToken } from "../../tokens/radii";

type Rmh = SemanticPalette;

type AlertVariantSpec = StateSpec & { bordered: boolean };

const variantStyle =
  (build: (rmh: Rmh) => AlertVariantSpec) =>
  ({ theme }: { theme: Theme }): CSSObject => {
    const spec = build(theme.vars!.palette.rmh);
    return {
      borderWidth: spec.bordered ? theme.spacing(0.25) : 0,
      ...stateToCss(spec),
    };
  };

// --- variant factories -----------------------------------------------------

// Standard: bordered + faint background per color
const standardError = (rmh: Rmh): AlertVariantSpec => ({
  bordered: true,
  backgroundColor: rmh.paper["9-errorFaint"].rest,
  borderColor: rmh.border["9-errorFaint"].rest,
});
const standardWarning = (rmh: Rmh): AlertVariantSpec => ({
  bordered: true,
  backgroundColor: rmh.paper["13-warningFaint"].rest,
  borderColor: rmh.border["13-warningFaint"].rest,
});
const standardInfo = (rmh: Rmh): AlertVariantSpec => ({
  bordered: true,
  backgroundColor: rmh.paper["7-brandFaint"].rest,
  borderColor: rmh.border["7-brandFaint"].rest,
});
const standardSuccess = (rmh: Rmh): AlertVariantSpec => ({
  bordered: true,
  backgroundColor: rmh.paper["11-successFaint"].rest,
  borderColor: rmh.border["11-successFaint"].rest,
});
const standardNeutral = (rmh: Rmh): AlertVariantSpec => ({
  bordered: true,
  backgroundColor: rmh.paper["4-faint"].rest,
  borderColor: rmh.border["4-faint"].rest,
});

// Outlined: border only, transparent background
const outlinedError = (rmh: Rmh): AlertVariantSpec => ({
  bordered: true,
  backgroundColor: "transparent",
  borderColor: rmh.border["8-error"].rest,
});
const outlinedWarning = (rmh: Rmh): AlertVariantSpec => ({
  bordered: true,
  backgroundColor: "transparent",
  borderColor: rmh.border["12-warning"].rest,
});
const outlinedInfo = (rmh: Rmh): AlertVariantSpec => ({
  bordered: true,
  backgroundColor: "transparent",
  borderColor: rmh.border["5-brand"].rest,
});
const outlinedSuccess = (rmh: Rmh): AlertVariantSpec => ({
  bordered: true,
  backgroundColor: "transparent",
  borderColor: rmh.border["10-success"].rest,
});
const outlinedNeutral = (rmh: Rmh): AlertVariantSpec => ({
  bordered: true,
  backgroundColor: "transparent",
  borderColor: rmh.border["1-surface"].rest,
});

// Filled: solid background only, no border
const filledError = (rmh: Rmh): AlertVariantSpec => ({
  bordered: false,
  backgroundColor: rmh.paper["8-errorFilled"].rest,
});
const filledWarning = (rmh: Rmh): AlertVariantSpec => ({
  bordered: false,
  backgroundColor: rmh.paper["12-warningFilled"].rest,
});
const filledInfo = (rmh: Rmh): AlertVariantSpec => ({
  bordered: false,
  backgroundColor: rmh.paper["5-brandFilled"].rest,
});
const filledSuccess = (rmh: Rmh): AlertVariantSpec => ({
  bordered: false,
  backgroundColor: rmh.paper["10-successFilled"].rest,
});
const filledNeutral = (rmh: Rmh): AlertVariantSpec => ({
  bordered: false,
  backgroundColor: rmh.paper["4-faint"].rest,
});

export const MuiAlert: NonNullable<Components<Theme>["MuiAlert"]> = {
  defaultProps: {
    variant: "standard",
    severity: "info",
    // No built-in icon for the augmented `neutral` severity — render nothing.
    iconMapping: { neutral: null },
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: RadiusToken.MD,
      padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
      gap: theme.spacing(2),
      alignItems: "flex-start",
      borderStyle: "solid",
      borderWidth: 0,
      borderColor: "transparent",
      transition: theme.transitions.create(
        ["background-color", "border-color"],
        { duration: theme.transitions.duration.shortest }
      ),
      "& .MuiAlert-icon": {
        marginRight: 0,
        padding: `${theme.spacing(1)} 0`,
      },
      "& .MuiAlert-message": {
        padding: `${theme.spacing(1)} 0`,
      },
      "& .MuiAlert-action": {
        padding: `${theme.spacing(1)} 0`,
      },
    }),
    message: ({ theme }) => ({
      ...theme.typography.bodyMdRegular,
    }),
  },
  variants: [
    {
      props: { variant: "standard", severity: "error" },
      style: variantStyle(standardError),
    },
    {
      props: { variant: "standard", severity: "warning" },
      style: variantStyle(standardWarning),
    },
    {
      props: { variant: "standard", severity: "info" },
      style: variantStyle(standardInfo),
    },
    {
      props: { variant: "standard", severity: "success" },
      style: variantStyle(standardSuccess),
    },
    {
      props: { variant: "standard", severity: "neutral" },
      style: variantStyle(standardNeutral),
    },
    {
      props: { variant: "outlined", severity: "error" },
      style: variantStyle(outlinedError),
    },
    {
      props: { variant: "outlined", severity: "warning" },
      style: variantStyle(outlinedWarning),
    },
    {
      props: { variant: "outlined", severity: "info" },
      style: variantStyle(outlinedInfo),
    },
    {
      props: { variant: "outlined", severity: "success" },
      style: variantStyle(outlinedSuccess),
    },
    {
      props: { variant: "outlined", severity: "neutral" },
      style: variantStyle(outlinedNeutral),
    },
    {
      props: { variant: "filled", severity: "error" },
      style: variantStyle(filledError),
    },
    {
      props: { variant: "filled", severity: "warning" },
      style: variantStyle(filledWarning),
    },
    {
      props: { variant: "filled", severity: "info" },
      style: variantStyle(filledInfo),
    },
    {
      props: { variant: "filled", severity: "success" },
      style: variantStyle(filledSuccess),
    },
    {
      props: { variant: "filled", severity: "neutral" },
      style: variantStyle(filledNeutral),
    },
  ],
};
